import { useRef, useEffect, useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Button } from '../../../../../components/ui/button';
import { Card } from '../../../../../components/ui/card';
import { Input } from '../../../../../components/ui/input';
import { Badge } from '../../../../../components/ui/badge';
import { motion } from 'framer-motion';
import { useExpandable } from '../../../../../hooks/use-expandable';
import { useImageUpload } from '../../../../../hooks/use-image-upload';
import { ImagePlus, ChevronDown, ChevronUp, Upload, Trash2, X, AlertCircle, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { cn } from '../../../../../lib/utils';
import { storageService } from '../../../services/storageService';
import { aiProcessingService } from '../../../services/aiProcessingService';
import { messagesApi } from '../../../services/messagesApi';
import { toast } from '@/hooks/use-toast';
import { AddNodeMenu } from './AddNodeMenu';

interface ImageMessageData {
  label?: string;
  imageUrl?: string;
  caption?: string;
  messageId?: string;
  aiDescription?: string;
  processingStatus?: 'pending' | 'processing' | 'completed' | 'error';
  flowId?: string;
  groupId?: string;
  sequenceOrder?: number;
}

export function ImageMessageNode({ id, data, isConnectable }: NodeProps<ImageMessageData>) {
  const contentRef = useRef<HTMLDivElement>(null);
  const { isExpanded, toggleExpand, animatedHeight } = useExpandable(false);
  const { setNodes, getNodes } = useReactFlow();
  const [caption, setCaption] = useState(data.caption || '');
  const [hasQuotes, setHasQuotes] = useState(false);
  const [aiDescription, setAiDescription] = useState(data.aiDescription || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<'pending' | 'processing' | 'completed' | 'error'>(
    data.processingStatus || 'pending'
  );

  const {
    previewUrl,
    fileName,
    fileInputRef,
    handleThumbnailClick,
    handleFileChange,
    handleRemove,
  } = useImageUpload({
    onUpload: (url) => console.log("Imagen cargada:", url),
    initialUrl: data.imageUrl,
    initialFileName: data.imageUrl ? (data.caption || 'Imagen guardada') : null,
  });

  useEffect(() => {
    if (contentRef.current) {
      animatedHeight.set(isExpanded ? contentRef.current.offsetHeight : 0);
    }
  }, [isExpanded, animatedHeight, previewUrl]);

  const handleCaptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newCaption = e.target.value;
    setCaption(newCaption);

    const hasQuotesDetected = /["']/.test(newCaption);
    setHasQuotes(hasQuotesDetected);
  };

  const handleSave = async () => {
    // Permitir actualizar si ya existe un messageId y imageUrl
    if (!previewUrl && !data.imageUrl) {
      toast.error('Debes seleccionar una imagen primero');
      return;
    }

    setIsSaving(true);
    setIsProcessing(true);
    setProcessingStatus('processing');

    try {
      // Obtener valores actualizados del nodo
      const currentNodes = getNodes();
      const currentNode = currentNodes.find(n => n.id === id);

      if (!currentNode) {
        throw new Error('Nodo no encontrado');
      }

      let finalImageUrl = data.imageUrl;
      let finalFileName = fileName;

      // 1. Solo subir si es una nueva imagen (blob URL) o si no hay imageUrl previa
      if (previewUrl && previewUrl.startsWith('blob:') && fileInputRef.current?.files?.[0]) {
        const file = fileInputRef.current.files[0];
        const uploadResult = await storageService.uploadFile(file, 'image');
        finalImageUrl = uploadResult.url;
        finalFileName = uploadResult.fileName;

        toast.info('Imagen subida, procesando con IA...');
      }

      // 2. Procesar con IA solo si es una imagen nueva
      let description = data.aiDescription || '';
      if (previewUrl && previewUrl.startsWith('blob:') && finalImageUrl) {
        description = await aiProcessingService.processImageDescription(finalImageUrl);
        setAiDescription(description);
      }

      setProcessingStatus('completed');

      // 3. Guardar en BD
      const savedMessage = await messagesApi.upsertMessage({
        id: data.messageId,
        node_id: id,
        node_type: 'image',
        text_content: caption,
        media_url: finalImageUrl!,
        file_name: finalFileName!,
        ai_description: description,
        ai_processing_status: 'completed',
        mime_type: fileInputRef.current?.files?.[0]?.type || 'image/jpeg',
        file_size: fileInputRef.current?.files?.[0]?.size || 0,
        flow_id: currentNode.data.flowId || null,
        group_id: currentNode.data.groupId || null,
        sequence_order: currentNode.data.sequenceOrder || 1,
      });

      // 4. Actualizar el nodo en ReactFlow
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? {
              ...node,
              data: {
                ...node.data,
                imageUrl: finalImageUrl,
                caption: caption,
                messageId: savedMessage.id,
                aiDescription: description,
                processingStatus: 'completed'
              }
            }
            : node
        )
      );

      toast.success(data.messageId ? 'Imagen actualizada y procesada' : 'Imagen guardada y procesada correctamente');
    } catch (error) {
      console.error('Error:', error);
      setProcessingStatus('error');
      toast.error('Error al guardar la imagen');
    } finally {
      setIsSaving(false);
      setIsProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!data.messageId) {
      toast.error('No hay mensaje guardado para eliminar');
      return;
    }

    if (!window.confirm('¿Estás seguro de eliminar esta imagen de la base de datos?')) {
      return;
    }

    try {
      await messagesApi.deleteMessage(id);

      // Actualizar el nodo en ReactFlow
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? {
              ...node,
              data: {
                ...node.data,
                messageId: undefined,
                aiDescription: undefined,
                processingStatus: 'pending'
              }
            }
            : node
        )
      );

      toast.success('Imagen eliminada de la base de datos');
    } catch (error) {
      console.error('Error eliminando mensaje:', error);
      toast.error('Error al eliminar la imagen');
    }
  };

  const getStatusBadge = () => {
    if (isSaving) {
      return (
        <Badge className="bg-blue-500">
          <Loader2 className="h-3 w-3 animate-spin" />
        </Badge>
      );
    }
    if (processingStatus === 'completed' && data.messageId) {
      return (
        <Badge className="bg-green-500">
          <CheckCircle className="h-3 w-3" />
        </Badge>
      );
    }
    if (processingStatus === 'error') {
      return (
        <Badge className="bg-red-500">
          <AlertTriangle className="h-3 w-3" />
        </Badge>
      );
    }
    return (
      <Badge className="bg-yellow-500">
        <AlertCircle className="h-3 w-3" />
      </Badge>
    );
  };

  return (
    <Card className="min-w-[300px] max-w-[400px] bg-white shadow-lg border-2 border-primary/20">
      {/* Número de secuencia - arriba izquierda */}
      {data.groupId && (
        <div className="absolute -top-3 -left-3 z-10">
          <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold shadow-lg border-2 border-white">
            {data.sequenceOrder || 1}
          </div>
        </div>
      )}

      <div className="absolute -top-2 -right-2 z-10">
        {getStatusBadge()}
      </div>

      <Handle
        type="target"
        position={Position.Left}
        isConnectable={isConnectable}
        className="w-5 h-5 !bg-primary hover:w-6 hover:h-6 transition-all cursor-crosshair"
        style={{ border: '3px solid white' }}
      />

      <div className="p-4">
        <div className="flex justify-between items-center gap-2 mb-2">
          <div className="flex items-center gap-2">
            <ImagePlus className="h-5 w-5 text-primary" />
            <h3 className="font-medium text-foreground">Enviar Imagen</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleExpand}
            className="h-8 w-8 p-0"
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>

        <Input
          type="file"
          accept="image/*"
          className="hidden"
          ref={fileInputRef}
          onChange={handleFileChange}
        />

        <motion.div
          style={{ height: animatedHeight }}
          className="overflow-hidden"
        >
          <div ref={contentRef} className="space-y-3 pt-2">
            {!previewUrl ? (
              <div
                onClick={handleThumbnailClick}
                className={cn(
                  "flex h-40 cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:bg-gray-100"
                )}
              >
                <div className="rounded-full bg-white p-3 shadow-sm">
                  <ImagePlus className="h-6 w-6 text-gray-400" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-700">Seleccionar imagen</p>
                  <p className="text-xs text-gray-500">JPG, PNG o GIF</p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="group relative h-40 overflow-hidden rounded-lg border">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 transition-opacity group-hover:opacity-100" />
                  <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={handleThumbnailClick}
                      className="h-9 w-9 p-0"
                    >
                      <Upload className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={handleRemove}
                      className="h-9 w-9 p-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <span className="truncate">{fileName}</span>
                  <button
                    onClick={handleRemove}
                    className="ml-auto rounded-full p-1 bg-red-500/10 hover:bg-red-500/20 text-red-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              </div>
            )}

            <div>
              <label className="text-xs text-gray-600 mb-1 block">
                Mensaje o Caption (opcional):
              </label>
              <textarea
                value={caption}
                onChange={handleCaptionChange}
                placeholder="Texto que acompañará la imagen..."
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 resize-none ${hasQuotes
                  ? 'border-red-500 focus:ring-red-500 bg-red-50'
                  : 'border-gray-300 focus:ring-primary'
                  }`}
                rows={3}
              />

              {hasQuotes && (
                <div className="flex items-start gap-2 mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">
                    Las comillas no están permitidas en los flujos de mensaje.
                  </p>
                </div>
              )}
            </div>

            {aiDescription && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <label className="text-xs font-semibold text-blue-700 mb-1 block">
                  Descripción generada por IA:
                </label>
                <p className="text-xs text-gray-700">
                  {aiDescription}
                </p>
              </div>
            )}

            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>{caption.length} caracteres</span>
            </div>

            {/* Botones dentro del área expandible */}
            {previewUrl && (
              <div className="flex items-center justify-end gap-2 w-full mt-3">
                {data.messageId && (
                  <Button
                    size="sm"
                    variant="destructive"
                    className="h-7 px-3 text-xs"
                    onClick={handleDelete}
                    disabled={isSaving}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                    Eliminar
                  </Button>
                )}
                <Button
                  size="sm"
                  className="h-7 px-3 text-xs flex-1 min-w-[100px]"
                  disabled={!previewUrl || hasQuotes || isSaving}
                  onClick={handleSave}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      {isProcessing ? 'Procesando...' : 'Guardando...'}
                    </>
                  ) : (
                    data.messageId ? 'Actualizar' : 'Guardar'
                  )}
                </Button>
              </div>
            )}
          </div>
        </motion.div>

        {!isExpanded && previewUrl && (
          <div className="mt-2 space-y-1">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <ImagePlus className="h-3 w-3" />
              <span className="truncate">{fileName || 'Imagen cargada'}</span>
            </div>
            {aiDescription && (
              <div className="text-xs text-blue-600 truncate">
                ✓ Procesada por IA
              </div>
            )}
          </div>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Right}
        isConnectable={isConnectable}
        className="w-5 h-5 !bg-primary hover:w-6 hover:h-6 transition-all cursor-crosshair"
        style={{ border: '3px solid white' }}
      />
      <AddNodeMenu nodeId={id} />
    </Card>
  );
}
